import React from "react";
import {Button, Dropdown, Input, Menu, Modal, Space, Table, Upload} from "antd";
import {DownloadOutlined, InboxOutlined, SearchOutlined, UploadOutlined} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import * as Setting from "./Setting";
import i18next from "i18next";
import BaseListPage from "./BaseListPage";
import * as PermissionPraisindoBackend from "./backend/PermissionPraisindoBackend";
import PopconfirmModal from "./common/modal/PopconfirmModal";

const {Dragger} = Upload;

const STORAGE_KEY = "permissionsPraisindoListPage_state";

class PermissionsPraisindoListPage extends BaseListPage {
  constructor(props) {
    super(props);

    let savedState = null;
    const savedStateStr = sessionStorage.getItem(STORAGE_KEY);
    savedState = savedStateStr ? JSON.parse(savedStateStr) : null;

    const newPagination = savedState?.pagination ? {
      current: savedState.pagination.current || 1,
      pageSize: savedState.pagination.pageSize || 10,
      total: savedState.pagination.total || 0,
    } : {
      current: 1,
      pageSize: 10,
      total: 0,
    };

    this.state = {
      ...this.state,
      organization: this.props.account,
      pagination: newPagination,
      searchText: savedState?.searchText || "",
      searchedColumn: savedState?.searchedColumn || "",
      sortField: savedState?.sortField || "",
      sortOrder: savedState?.sortOrder || "",
      loading: false,
      importModalVisible: false,
    };
  }

  UNSAFE_componentWillMount() {
    // Don't call parent's UNSAFE_componentWillMount to prevent duplicate fetch
    // We'll handle fetching in componentDidMount instead
  }

  componentDidMount() {
    const savedState = this.loadState();
    if (savedState) {
      this.setState({
        pagination: savedState.pagination,
        searchText: savedState.searchText,
        searchedColumn: savedState.searchedColumn,
        sortField: savedState.sortField,
        sortOrder: savedState.sortOrder,
      }, () => {
        this.fetch({
          pagination: this.state.pagination,
          searchText: this.state.searchText,
          searchedColumn: this.state.searchedColumn,
          sortField: this.state.sortField,
          sortOrder: this.state.sortOrder,
        }, false);
      });
    } else {
      this.fetch({
        pagination: this.state.pagination,
        searchText: this.state.searchText,
        searchedColumn: this.state.searchedColumn,
        sortField: this.state.sortField,
        sortOrder: this.state.sortOrder,
      }, false);
    }
  }

  loadState = () => {
    try {
      const savedState = sessionStorage.getItem(STORAGE_KEY);
      return savedState ? JSON.parse(savedState) : null;
    } catch (error) {
      return null;
    }
  };

  saveState = () => {
    try {
      const stateToSave = {
        pagination: {
          current: this.state.pagination.current,
          pageSize: this.state.pagination.pageSize,
          total: this.state.pagination.total,
        },
        searchText: this.state.searchText,
        searchedColumn: this.state.searchedColumn,
        sortField: this.state.sortField,
        sortOrder: this.state.sortOrder,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch {
      throw new Error("Failed to save state");
    }
  };

  clearState = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      throw new Error("Failed to clear state");
    }
  };

  getColumnSearchProps = dataIndex => ({
    filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => (
      <div style={{padding: 8}}>
        <Input
          ref={node => {
            this.searchInput = node;
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
          style={{marginBottom: 8, display: "block"}}
        />

        <Space>
          <Button
            type="primary"
            onClick={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{width: 90}}
          >
            Search
          </Button>
          <Button onClick={() => this.handleReset(clearFilters)} size="small" style={{width: 90}}>
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({closeDropdown: false});
              this.setState({
                searchText: selectedKeys[0],
                searchedColumn: dataIndex,
              });
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{color: filtered ? "#1890ff" : undefined}} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: visible => {
      if (visible) {
        setTimeout(() => this.searchInput && this.searchInput.select(), 100);
      }
    },
    filteredValue: this.state.searchedColumn === dataIndex && this.state.searchText ? [this.state.searchText] : null,
    render: text =>
      this.state.searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{backgroundColor: "#ffc069", padding: 0}}
          searchWords={[this.state.searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  handleSearch = (selectedKeys, confirm, dataIndex) => {
    if (!selectedKeys || !selectedKeys[0] || selectedKeys[0].trim() === "") {
      return;
    }
    confirm();
    this.setState({
      searchText: selectedKeys[0],
      searchedColumn: dataIndex,
    });
    this.fetch({
      searchText: selectedKeys[0],
      searchedColumn: dataIndex,
      pagination: {...this.state.pagination, current: 1},
      sortField: this.state.sortField,
      sortOrder: this.state.sortOrder,
    });
  };

  handleReset = (clearFilters) => {
    clearFilters();
    this.clearState();
    this.setState({
      searchText: "",
      searchedColumn: "",
      sortField: "",
      sortOrder: "",
    });
    this.fetch({
      pagination: {...this.state.pagination, current: 1},
      searchText: "",
      searchedColumn: "",
      sortField: "",
      sortOrder: "",
    });
  };

  handleTableChange = (pagination, filters, sorter) => {
    this.fetch({
      sortField: sorter.field,
      sortOrder: sorter.order,
      pagination,
      ...filters,
      searchText: this.state.searchText,
      searchedColumn: this.state.searchedColumn,
    });
  };

  deletePermissionPraisindo = (id, owner) => {
    PermissionPraisindoBackend.deletePermission(id, owner)
      .then((res) => {
        if (res.status === "ok") {
          Setting.showMessage("success", "Successfully done");
          this.fetch({
            pagination: {...this.state.pagination},
            searchText: this.state.searchText,
            searchedColumn: this.state.searchedColumn,
            sortField: this.state.sortField,
            sortOrder: this.state.sortOrder,
          }, true);
        } else {
          Setting.showMessage("error", `${res.msg}`);
        }
      });
  };

  handleExport = (exportAll = false) => {
    const {pagination, searchText, searchedColumn, sortField, sortOrder} = this.state;

    this.setState({loading: true});

    const page = exportAll ? "" : pagination.current;
    const pageSize = exportAll ? "" : pagination.pageSize;

    PermissionPraisindoBackend.exportPermissionsPraisindo(
      page,
      pageSize,
      searchedColumn,
      searchText,
      sortField,
      sortOrder
    )
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = exportAll ? "permissions_export_all.xlsx" : "permissions_export_page.xlsx";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        Setting.showMessage("success", "Successfully exported");
      })
      .catch((error) => {
        Setting.showMessage("error", `Failed to export: ${error}`);
      })
      .finally(() => {
        this.setState({loading: false});
      });
  };

  showImportModal = () => {
    this.setState({importModalVisible: true});
  };

  hideImportModal = () => {
    this.setState({importModalVisible: false});
  };

  handleImport = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    this.setState({loading: true});
    PermissionPraisindoBackend.importPermissionsPraisindo(formData)
      .then((res) => {
        if (res.status === "ok") {
          Setting.showMessage("success", "Successfully done");
          this.hideImportModal();
          this.fetch({
            pagination: {...this.state.pagination},
            searchText: this.state.searchText,
            searchedColumn: this.state.searchedColumn,
            sortField: this.state.sortField,
            sortOrder: this.state.sortOrder,
          });
        } else {
          Setting.showMessage("error", `${res.msg.charAt(0).toUpperCase()}${res.msg.slice(1)}`);
        }
      })
      .catch((error) => {
        Setting.showMessage("error", `${error}`);
      })
      .finally(() => {
        this.setState({loading: false});
      });
    return false;
  };

  renderAdd() {
    return (
      <Button type="primary" size="small"
        onClick={() => {
          this.saveState();
          this.props.history.push({
            pathname: "/role-permissions/add",
            mode: "add",
          });
        }}
      >
        {i18next.t("user:Add")}
      </Button>
    );
  }

  renderExportImport() {
    const {loading} = this.state;

    const exportMenu = (
      <Menu>
        <Menu.Item
          key="current"
          onClick={() => this.handleExport(false)}
        >
          {i18next.t("general:Export Current Page")}
        </Menu.Item>
        <Menu.Item
          key="all"
          onClick={() => this.handleExport(true)}
        >
          {i18next.t("general:Export All")}
        </Menu.Item>
      </Menu>
    );

    return (
      <Space>
        <Dropdown overlay={exportMenu} trigger={["click"]}>
          <Button
            type="default"
            size="small"
            icon={<DownloadOutlined />}
            loading={loading} // Add loading indicator
          >
            {i18next.t("general:Export")}
          </Button>
        </Dropdown>
        <Button
          type="default"
          size="small"
          icon={<UploadOutlined />}
          onClick={this.showImportModal}
        >
          {i18next.t("general:Import")}
        </Button>
      </Space>
    );
  }

  renderImportModal() {
    const draggerProps = {
      name: "file",
      multiple: false,
      accept: ".xlsx",
      beforeUpload: this.handleImport,
      showUploadList: true,
      onDrop(e) {
        // console.log("Dropped files", e.dataTransfer.files);
      },
    };

    return (
      <Modal
        title={i18next.t("general:Import Permissions")}
        open={this.state.importModalVisible}
        onCancel={this.hideImportModal}
        footer={null}
        width={600}
      >
        <Dragger {...draggerProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {i18next.t("general:Click or drag file to this area to upload")}
          </p>
          <p className="ant-upload-hint">
            {i18next.t("general:Support XLSX file format")}
          </p>
        </Dragger>
      </Modal>
    );
  }

  renderTable(permissions) {
    const columns = [
      {
        title: i18next.t("general:Owner"),
        dataIndex: "owner",
        key: "owner",
        width: Setting.isMobile() ? "100px" : "120px",
        fixed: "left",
        sorter: true,
        ...this.getColumnSearchProps("owner"),
        render: (text) => <>{text}</>,
      },
      {
        title: i18next.t("general:Service"),
        dataIndex: "service",
        key: "service",
        width: Setting.isMobile() ? "100px" : "120px",
        fixed: "left",
        sorter: true,
        ...this.getColumnSearchProps("service"),
        render: (text) => <>{text}</>,
      },
      {
        title: i18next.t("general:Feature"),
        dataIndex: "feature",
        key: "feature",
        width: Setting.isMobile() ? "80px" : "110px",
        fixed: "left",
        sorter: true,
        ...this.getColumnSearchProps("feature"),
        render: (text) => <p>{text}</p>,
      },
      {
        title: i18next.t("general:Permission Action"),
        dataIndex: "action",
        key: "action",
        width: Setting.isMobile() ? "80px" : "110px",
        fixed: "left",
        sorter: true,
        ...this.getColumnSearchProps("action"),
        render: (text) => <>{text}</>,
      },
      {
        title: i18next.t("general:Label"),
        dataIndex: "label",
        key: "label",
        width: Setting.isMobile() ? "80px" : "110px",
        fixed: "left",
        sorter: true,
        ...this.getColumnSearchProps("label"),
        render: (text) => <>{text}</>,
      },
      {
        title: i18next.t("general:Action"),
        dataIndex: "actions",
        key: "actions",
        width: "50px",
        fixed: Setting.isMobile() ? false : "right",
        render: (text, record, index) => {
          return (
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  this.saveState();
                  this.props.history.push({
                    pathname: `/role-permissions/${record.owner}/${record.id}`,
                    permissionData: record,
                  });
                }}
              >
                {i18next.t("general:Edit")}
              </Button>
              <PopconfirmModal
                text={i18next.t("general:Delete")}
                title={`${i18next.t("general:Sure to delete")}: ${record?.service}/${record?.feature} ?`}
                onConfirm={() => this.deletePermissionPraisindo(record?.id, record?.owner)}
              />
            </Space>
          );
        },
      },
    ];

    const paginationProps = {
      current: this.state.pagination.current,
      pageSize: this.state.pagination.pageSize,
      total: this.state.pagination.total,
      showQuickJumper: true,
      showSizeChanger: true,
      showTotal: () =>
        i18next
          .t("general:{total} in total")
          .replace("{total}", this.state.pagination.total),
    };

    return (
      <div>
        <Table
          scroll={{x: "max-content"}}
          columns={columns}
          dataSource={permissions}
          rowKey={(record) => `${record.owner}/${record.name}`}
          size="middle"
          bordered
          pagination={paginationProps}
          onChange={this.handleTableChange}
          title={() => (
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <div>
                {i18next.t("general:Permissions")}&nbsp;&nbsp;&nbsp;&nbsp;
                {this.renderAdd()}
              </div>
              {this.renderExportImport()}
            </div>
          )}
          loading={this.state.loading}
        />
        {this.renderImportModal()}
      </div>
    );
  }

  fetch = (params = {}, shouldSaveState = true) => {
    let field = params.searchedColumn, value = params.searchText;
    const sortField = params.sortField, sortOrder = params.sortOrder;
    if (params.type !== undefined && params.type !== null) {
      field = "type";
      value = params.type;
    }
    this.setState({loading: true, data: []});
    (Setting.isDefaultOrganizationSelected(this.props.account) ? PermissionPraisindoBackend.getPermissionPraisindo("", params.pagination.current, params.pagination.pageSize, field, value, sortField, sortOrder) : PermissionPraisindoBackend.getPermissionPraisindo(Setting.getRequestOrganization(this.props.account), params.pagination.current, params.pagination.pageSize, field, value, sortField, sortOrder))
      .then((res) => {
        this.setState({
          loading: false,
        });
        if (res.status === "ok") {
          const newTotal = res.data2;
          const maxPage = Math.ceil(newTotal / params.pagination.pageSize);
          let currentPage = params.pagination.current;

          if (currentPage > maxPage && maxPage > 0) {
            currentPage = maxPage;
          } else if (maxPage === 0) {
            currentPage = 1;
          }

          this.setState({
            data: res.data,
            pagination: {
              ...params.pagination,
              current: currentPage,
              total: newTotal,
            },
            searchText: params.searchText,
            searchedColumn: params.searchedColumn,
            sortField: sortField,
            sortOrder: sortOrder,
          }, () => {
            if (currentPage !== params.pagination.current) {
              this.fetch({
                pagination: {
                  ...params.pagination,
                  current: currentPage,
                },
                searchText: params.searchText,
                searchedColumn: params.searchedColumn,
                sortField: sortField,
                sortOrder: sortOrder,
              }, shouldSaveState);
            } else if (shouldSaveState) {
              this.saveState();
            }
          });
        } else {
          if (Setting.isResponseDenied(res)) {
            this.setState({
              isAuthorized: false,
            });
          } else {
            Setting.showMessage("error", res.msg);
          }
        }
      });
  };
}

export default PermissionsPraisindoListPage;
